import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, TextField, Typography,
} from '@mui/material';
import { attendanceApi, ShiftDay } from '../../services/attendanceApi';
import { WEEKDAYS_LONG } from './attendanceFormat';

interface Props {
  open: boolean;
  staffName: string;
  studioUserId: string;
  studioId?: string;
  days: ShiftDay[];
  /** Pre-fill when nothing is set yet (e.g. the instructor's existing shift). */
  suggested?: { start: string; end: string } | null;
  onClose: () => void;
  onSaved: (days: ShiftDay[]) => void;
}

type Row = { on: boolean; start: string; end: string };
const ORDER = [1, 2, 3, 4, 5, 6, 0]; // Monday first

/**
 * Weekly shift for one staff member. Unticked days are days off. Times are IST;
 * a shift must end on the same day it starts.
 */
const ShiftEditorDialog: React.FC<Props> = ({
  open, staffName, studioUserId, studioId, days, suggested, onClose, onSaved,
}) => {
  const [rows, setRows] = useState<Record<number, Row>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const fresh: Record<number, Row> = {};
    const fallback = suggested ?? { start: '09:00', end: '18:00' };
    for (const wd of ORDER) {
      const d = days.find((x) => x.weekday === wd);
      fresh[wd] = d
        ? { on: true, start: d.start, end: d.end }
        : { on: !days.length && !!suggested && wd !== 0, start: fallback.start, end: fallback.end };
    }
    setRows(fresh);
    setError(null);
  }, [open, days, suggested]);

  const update = (wd: number, patch: Partial<Row>) =>
    setRows((r) => ({ ...r, [wd]: { ...r[wd], ...patch } }));

  const copyMondayToWeekdays = () =>
    setRows((r) => {
      const mon = r[1];
      const next = { ...r };
      for (const wd of [2, 3, 4, 5]) next[wd] = { ...mon };
      return next;
    });

  const save = async () => {
    const out: ShiftDay[] = ORDER.filter((wd) => rows[wd]?.on).map((wd) => ({
      weekday: wd,
      start: rows[wd].start,
      end: rows[wd].end,
    }));
    const bad = out.find((d) => !d.start || !d.end || d.end <= d.start);
    if (bad) {
      setError(`${WEEKDAYS_LONG[bad.weekday]}: the shift must end after it starts.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await attendanceApi.setShift(studioUserId, out, studioId);
      onSaved(out);
    } catch (e: any) {
      setError(e?.message || 'Could not save the shift');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Shift · {staffName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Tick the days they work and set their hours. Arriving or leaving within the grace period counts as on time.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box display="flex" flexDirection="column" gap={1.25}>
          {ORDER.map((wd) => {
            const r = rows[wd];
            if (!r) return null;
            return (
              <Box key={wd} display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <FormControlLabel
                  sx={{ width: 140, m: 0 }}
                  control={<Checkbox checked={r.on} onChange={(e) => update(wd, { on: e.target.checked })} />}
                  label={WEEKDAYS_LONG[wd]}
                />
                {r.on ? (
                  <>
                    <TextField
                      type="time" size="small" label="Start" value={r.start}
                      onChange={(e) => update(wd, { start: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 130 }}
                    />
                    <TextField
                      type="time" size="small" label="End" value={r.end}
                      onChange={(e) => update(wd, { end: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 130 }}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.disabled">Day off</Typography>
                )}
              </Box>
            );
          })}
        </Box>
        <Button size="small" sx={{ mt: 1.5 }} onClick={copyMondayToWeekdays} disabled={!rows[1]?.on}>
          Copy Monday to Tue–Fri
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save shift
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftEditorDialog;
